import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { API_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const DocumentList = ({ refreshTrigger }) => {
  const { getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    module: '',
    subModule: '',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    document: null,
    loading: false,
  });
  const [editDialog, setEditDialog] = useState({
    open: false,
    document: null,
    loading: false,
    formData: {}
  });
  const [updatingTrackable, setUpdatingTrackable] = useState(null);

  // Predefined modules - can be expanded in the future
  const predefinedModules = [
    'Induction',
    'Training',
  ];

  // Helper function to convert mm:ss to total seconds
  const parseTimeToSeconds = (timeString) => {
    if (!timeString) return 0;
    const parts = timeString.split(':');
    if (parts.length === 1) {
      // Just minutes
      return parseInt(parts[0]) * 60 || 0;
    } else if (parts.length === 2) {
      // mm:ss format
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      // Validate seconds (must be 0-59)
      if (seconds > 59) {
        throw new Error('Seconds must be between 0 and 59');
      }
      return minutes * 60 + seconds;
    }
    return 0;
  };

  // Helper function to convert seconds to mm:ss
  const formatSecondsToTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to validate mm:ss format
  const isValidTimeFormat = (timeString) => {
    if (!timeString) return true; // Empty is valid (optional field)
    const regex = /^(\d{1,3}):([0-5]\d)$|^\d{1,3}$/;
    return regex.test(timeString);
  };

  // Get unique submodules for a selected module
  const getUniqueSubModules = (selectedModule) => {
    if (!selectedModule) return [];
    const uniqueSubModules = [...new Set(
      documents.filter(doc => doc.module === selectedModule).map(doc => doc.subModule)
    )];
    return uniqueSubModules.sort();
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setError('');

    try {
      // Use the new getDocumentsByModules endpoint with Induction and Training modules
      const modulesArray = ["Induction", "Training"];
      const queryParams = new URLSearchParams();
      queryParams.append('modules', JSON.stringify(modulesArray));
      
      // Add additional filters if they exist
      if (filters.subModule) queryParams.append('subModule', filters.subModule);

      const response = await fetch(`${API_URL}/documents/modules?${queryParams}`, {
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (response.ok) {
        setDocuments(result.documents || []);
      } else {
        setError(result.error || 'Failed to fetch documents');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [refreshTrigger]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      module: '',
      subModule: '',
    });
  };

  const handleDownload = async (fileName, documentName) => {
    try {
      const response = await fetch(`${API_URL}/documents/stream/${fileName}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documentName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download document');
      }
    } catch (err) {
      setError('Download failed. Please try again.');
    }
  };

  const handleView = (fileName) => {
    window.open(`${API_URL}/documents/stream/${fileName}`, '_blank');
  };

  const handleDeleteClick = (document) => {
    setDeleteDialog({
      open: true,
      document: document,
      loading: false,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.document) return;

    setDeleteDialog(prev => ({ ...prev, loading: true }));

    try {
      const response = await fetch(`${API_URL}/documents/${deleteDialog.document.documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        // Remove the document from the local state
        setDocuments(prev => prev.filter(doc => doc.documentId !== deleteDialog.document.documentId));
        setDeleteDialog({ open: false, document: null, loading: false });
      } else {
        const result = await response.json();
        setError(result.error || 'Failed to delete document');
        setDeleteDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      setError('Delete failed. Please try again.');
      setDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, document: null, loading: false });
  };

  const handleEditClick = (document) => {
    setEditDialog({
      open: true,
      document: document,
      loading: false,
      formData: {
        documentName: document.documentName,
        description: document.description || '',
        requiredReviewTime: formatSecondsToTime(document.requiredReviewTime || 0), // Convert seconds to mm:ss
      }
    });
  };

  const handleEditChange = (field, value) => {
    setEditDialog(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value
      }
    }));
  };

  const handleEditConfirm = async () => {
    if (!editDialog.document) return;

    // Validate time format
    if (editDialog.formData.requiredReviewTime && 
        !isValidTimeFormat(editDialog.formData.requiredReviewTime)) {
      setError('Invalid time format. Use MM:SS (e.g., 5:30) or just MM (e.g., 5)');
      return;
    }

    setEditDialog(prev => ({ ...prev, loading: true }));

    try {
      // Parse time string to seconds
      let timeInSeconds = 0;
      try {
        timeInSeconds = parseTimeToSeconds(editDialog.formData.requiredReviewTime);
      } catch (err) {
        setError(err.message);
        setEditDialog(prev => ({ ...prev, loading: false }));
        return;
      }

      const response = await fetch(`${API_URL}/documents/${editDialog.document.documentId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentName: editDialog.formData.documentName,
          description: editDialog.formData.description,
          requiredReviewTime: timeInSeconds,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Update the document in local state
        setDocuments(prev => prev.map(doc => 
          doc.documentId === editDialog.document.documentId 
            ? result.document
            : doc
        ));
        setEditDialog({ open: false, document: null, loading: false, formData: {} });
      } else {
        const result = await response.json();
        setError(result.error || 'Failed to update document');
        setEditDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      setError('Update failed. Please try again.');
      setEditDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleEditCancel = () => {
    setEditDialog({ open: false, document: null, loading: false, formData: {} });
  };

  const handleTrackableToggle = async (documentId, currentTrackableStatus) => {
    setUpdatingTrackable(documentId);
    
    try {
      const response = await fetch(`${API_URL}/documents/${documentId}/trackable`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isTrackable: !currentTrackableStatus
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Update the document in local state, using the backend response to stay in sync
        setDocuments(prev => prev.map(doc => 
          doc.documentId === documentId 
            ? { ...doc, isTrackable: !currentTrackableStatus, requiredReviewTime: result.document?.requiredReviewTime ?? doc.requiredReviewTime }
            : doc
        ));
      } else {
        const result = await response.json();
        setError(result.error || 'Failed to update trackable status');
      }
    } catch (err) {
      setError('Update failed. Please try again.');
    } finally {
      setUpdatingTrackable(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileIcon = (docType) => {
    if (docType.includes('pdf')) return '📄';
    if (docType.includes('word') || docType.includes('document')) return '📝';
    if (docType.includes('excel') || docType.includes('spreadsheet')) return '📊';
    if (docType.includes('powerpoint') || docType.includes('presentation')) return '📈';
    if (docType.includes('image')) return '🖼️';
    return '📎';
  };

  const filteredDocuments = documents.filter(doc => {
    // Filter by module (only Induction and Training are available)
    if (filters.module && doc.module !== filters.module) return false;
    // Filter by subModule
    if (filters.subModule && doc.subModule !== filters.subModule) return false;
    return true;
  });

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Document Library
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FilterIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Module</InputLabel>
              <Select
                value={filters.module}
                onChange={(e) => handleFilterChange('module', e.target.value)}
                label="Module"
              >
                <MenuItem value="">All Modules ({documents.length})</MenuItem>
                {predefinedModules.map((module) => {
                  const moduleCount = documents.filter(doc => doc.module === module).length;
                  return (
                    <MenuItem key={module} value={module}>
                      {module} ({moduleCount})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Sub Module</InputLabel>
              <Select
                value={filters.subModule}
                onChange={(e) => handleFilterChange('subModule', e.target.value)}
                label="Sub Module"
                disabled={!filters.module}
              >
                <MenuItem value="">All Sub Modules</MenuItem>
                {filters.module && getUniqueSubModules(filters.module).map((subModule) => {
                  const subModuleCount = documents.filter(doc => 
                    doc.module === filters.module && doc.subModule === subModule
                  ).length;
                  return (
                    <MenuItem key={subModule} value={subModule}>
                      {subModule} ({subModuleCount})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ height: 56 }}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Documents Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredDocuments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No documents found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filters.module || filters.subModule 
                ? 'Try adjusting your filters' 
                : 'Upload some documents to get started'}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Trackable</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Document</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Module</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Sub Module</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Review Time (mm:ss)</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Uploaded By</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Upload Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map((doc) => (
                  <TableRow key={doc.documentId} hover>
                    <TableCell>
                      <Checkbox
                        checked={doc.isTrackable}
                        onChange={() => handleTrackableToggle(doc.documentId, doc.isTrackable)}
                        disabled={updatingTrackable === doc.documentId}
                        color="primary"
                        sx={{
                          '&.Mui-checked': {
                            color: 'success.main',
                          },
                        }}
                      />
                      {updatingTrackable === doc.documentId && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">
                          {getFileIcon(doc.docType)}
                        </Typography>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {doc.documentName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doc.docType}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          '&:hover': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word'
                          }
                        }}
                        title={doc.description || 'No description'}
                      >
                        {doc.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={doc.module} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={doc.subModule} 
                        size="small" 
                        color="secondary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {doc.requiredReviewTime ? formatSecondsToTime(doc.requiredReviewTime) : 'Not set'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {doc.uploadedBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(doc.uploadedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Document">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(doc)}
                            color="info"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Document">
                          <IconButton
                            size="small"
                            onClick={() => handleView(doc.filePath)}
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Document">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc.filePath, doc.documentName)}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Document">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(doc)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Total Documents: {filteredDocuments.length}
          </Typography>
          <Button
            variant="outlined"
            onClick={fetchDocuments}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the document "{deleteDialog.document?.documentName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The document will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel}
            disabled={deleteDialog.loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteDialog.loading}
            startIcon={deleteDialog.loading ? <CircularProgress size={16} /> : null}
          >
            {deleteDialog.loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={handleEditCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Document
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Document Name"
              value={editDialog.formData.documentName || ''}
              onChange={(e) => handleEditChange('documentName', e.target.value)}
              disabled={editDialog.loading}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={editDialog.formData.description || ''}
              onChange={(e) => handleEditChange('description', e.target.value)}
              disabled={editDialog.loading}
              placeholder="Enter document description"
            />
            <TextField
              fullWidth
              label="Required Review Time (mm:ss)"
              value={editDialog.formData.requiredReviewTime || ''}
              onChange={(e) => handleEditChange('requiredReviewTime', e.target.value)}
              disabled={editDialog.loading}
              placeholder="Enter time (e.g., 5:30 or just 5)"
              helperText="Format: MM:SS or just MM. Example: 5:30 or 5. Set to 0 for no requirement."
              error={!isValidTimeFormat(editDialog.formData.requiredReviewTime) && editDialog.formData.requiredReviewTime !== ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEditCancel}
            disabled={editDialog.loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditConfirm}
            color="primary"
            variant="contained"
            disabled={editDialog.loading}
            startIcon={editDialog.loading ? <CircularProgress size={16} /> : null}
          >
            {editDialog.loading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DocumentList; 